import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import StorageAddForm from './StorageAddForm';
import StorageNodeActions, { StorageNodeRemoveButton } from './StorageNodeActions';
import StorageNodeIcon from './StorageNodeIcon';
import { logInfo } from '../../config_and_helpers/logger';
import {
  buildStorageListRows,
  countChildren,
  countLotsInside,
  formatStorageNodeCountLabel,
  getStorageNodeTypeLabel,
  listAncestorIds,
  matchStorageTree,
} from '../../utils/storageTree';
import './StorageExplorerStyle.css';

const LOG_CONTEXT = 'StorageNodeList';

/**
 * One place in the list. Clicking it opens what's inside, the same as clicking
 * its card on the map. The chevron only opens and closes the row.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode} props.node
 * @param {number} props.depth
 * @param {boolean} props.hasChildren
 * @param {boolean} props.expanded
 * @param {boolean} props.selected
 * @param {number} props.childCount
 * @param {number|null} props.lotCount
 * @param {Function} props.onToggle - (nodeId) => void
 * @param {Function} props.onOpen - (node) => void
 * @param {Function} [props.rowRef] - (nodeId, element|null) => void, so a reveal can scroll to it
 */
const StorageNodeListRow = ({
  node,
  depth,
  hasChildren,
  expanded,
  selected,
  childCount,
  lotCount,
  nodes,
  onToggle,
  onOpen,
  onAdd,
  onRemove,
  onDeleted,
  onOpenBox,
  onEditBox,
  onMove,
  onRename,
  onPlaceItem,
  onPreviewPlace,
  onCancelPreview,
  rowRef,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const nodeId = String(node.id);

  const className = [
    'storage-list__row',
    selected ? 'storage-list__row--selected' : '',
    formOpen ? 'storage-list__row--form-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li
      className="storage-list__item"
      style={{ '--storage-list-depth': depth }}
      ref={element => rowRef?.(nodeId, element)}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={className}
        onClick={() => onOpen?.(node)}
        onMouseEnter={() => onPreviewPlace?.(node)}
        onMouseLeave={() => onCancelPreview?.(node)}
      >
        {hasChildren ? (
          <Button
            variant="link"
            className="storage-list__chevron"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
            onClick={event => {
              event.stopPropagation();
              onToggle?.(nodeId);
            }}
          >
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Button>
        ) : (
          <span className="storage-list__chevron-spacer" aria-hidden="true" />
        )}

        <StorageNodeIcon node={node} className="storage-list__icon" />

        <Button
          variant="link"
          className="storage-list__name"
          aria-current={selected ? 'true' : undefined}
          onClick={event => {
            event.stopPropagation();
            onOpen?.(node);
          }}
        >
          {node.name}
        </Button>

        <span className="storage-list__meta">{getStorageNodeTypeLabel(node)}</span>
        <span className="storage-list__meta">
          {formatStorageNodeCountLabel({ node, childCount, lotCount })}
        </span>

        <div className="storage-list__actions">
          <StorageNodeActions
            node={node}
            nodes={nodes}
            onAdd={onAdd}
            onOpenBox={onOpenBox}
            onEditBox={onEditBox}
            onMove={onMove}
            onRename={onRename}
            onPlaceItem={onPlaceItem}
            onFormOpenChange={setFormOpen}
            buttonClassName="storage-list__action"
            addButtonClassName="storage-list__action"
            addFormIdPrefix={`list-${nodeId}`}
          />
          {onRemove ? (
            <StorageNodeRemoveButton
              node={node}
              childCount={childCount}
              onRemove={onRemove}
              onDeleted={onDeleted}
              className="storage-list__remove"
            />
          ) : null}
        </div>
      </div>
    </li>
  );
};

/**
 * The storage tree as an interactive list, the second view at `/storage`.
 *
 * Every place in the lab is here — rooms, units, shelves, drawers, racks, and
 * boxes — one row per place, opened and closed with its chevron. A row does
 * everything its map card does (Place item, Open chart, Edit, Move, Rename,
 * Add, Remove) through the shared `StorageNodeActions`, and clicking it opens
 * what's inside, so the two views cannot drift apart.
 *
 * The page owns which rows are open (`expandedIds`), so switching views does
 * not collapse the list. Searching a place name also matches its ancestors, so
 * searching a freezer lists what is inside it; those paths open on their own
 * without changing what the user opened by hand.
 *
 * @param {Object} props - Same place callbacks as `StorageMap`
 * @param {import('../../utils/storageTree').StorageNode[]} props.nodes
 * @param {string|null} [props.selectedId]
 * @param {Function} [props.onSelect] - (nodeId|null) => void
 * @param {Function} [props.onOpenNode] - (node) => void. Contents or the slot chart
 * @param {Object<string, number>|null} [props.lotCounts]
 * @param {Set<string>|Array} [props.expandedIds] - Rows the user opened
 * @param {Function} [props.onExpandedChange] - (Set|updater) => void
 * @param {number} [props.revealSignal] - Bump to open the path to the selected place and scroll to it
 */
const StorageNodeList = ({
  nodes = [],
  selectedId = null,
  onSelect,
  onAdd,
  onRemove,
  onDeleted,
  onOpenNode,
  onOpenBox,
  onEditBox,
  onMove,
  onRename,
  onPlaceItem,
  onPreviewPlace,
  onCancelPreview,
  lotCounts = null,
  expandedIds,
  onExpandedChange,
  revealSignal = 0,
}) => {
  const [query, setQuery] = useState('');
  const [addingRoom, setAddingRoom] = useState(false);
  const rowElementsRef = useRef(new Map());
  const pendingRevealRef = useRef(null);

  const { visibleIds, matchedIds } = useMemo(
    () => matchStorageTree(nodes, query),
    [nodes, query]
  );

  // A search opens the places above each match so the match can be seen. What
  // the user opened by hand is kept, so clearing the search restores their view.
  const effectiveExpandedIds = useMemo(() => {
    const base = new Set(expandedIds ? Array.from(expandedIds).map(String) : []);
    if (!visibleIds) return base;
    matchedIds.forEach(id => {
      listAncestorIds(nodes, id).forEach(ancestorId => base.add(ancestorId));
    });
    return base;
  }, [expandedIds, matchedIds, nodes, visibleIds]);

  const rows = useMemo(
    () => buildStorageListRows(nodes, { expandedIds: effectiveExpandedIds, visibleIds }),
    [nodes, effectiveExpandedIds, visibleIds]
  );

  const rowCounts = useMemo(() => {
    const counts = new Map();
    rows.forEach(row => {
      counts.set(row.id, {
        childCount: countChildren(nodes, row.node.id),
        lotCount: lotCounts ? countLotsInside(nodes, row.node.id, lotCounts) : null,
      });
    });
    return counts;
  }, [rows, nodes, lotCounts]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    logInfo(LOG_CONTEXT, 'storage list search', {
      queryLength: trimmed.length,
      matchCount: matchedIds.size,
      rowCount: rows.length,
    });
  }, [query, matchedIds.size, rows.length]);

  const handleToggle = useCallback(
    nodeId => {
      const key = String(nodeId);
      onExpandedChange?.(previous => {
        const next = new Set(previous ? Array.from(previous).map(String) : []);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        logInfo(LOG_CONTEXT, 'storage list row toggled', {
          nodeId: key,
          expanded: next.has(key),
        });
        return next;
      });
    },
    [onExpandedChange]
  );

  const handleOpen = useCallback(
    node => {
      if (!node) return;
      onSelect?.(node.id);
      logInfo(LOG_CONTEXT, 'storage list row opened', {
        nodeId: node.id,
        kind: node.kind,
      });
      onOpenNode?.(node);
    },
    [onOpenNode, onSelect]
  );

  const parentIds = useMemo(
    () =>
      nodes
        .filter(node => node && node.id != null && countChildren(nodes, node.id) > 0)
        .map(node => String(node.id)),
    [nodes]
  );

  const handleExpandAll = useCallback(() => {
    logInfo(LOG_CONTEXT, 'storage list expand all', { placeCount: parentIds.length });
    onExpandedChange?.(new Set(parentIds));
  }, [onExpandedChange, parentIds]);

  const handleCollapseAll = useCallback(() => {
    logInfo(LOG_CONTEXT, 'storage list collapse all', { placeCount: parentIds.length });
    onExpandedChange?.(new Set());
  }, [onExpandedChange, parentIds.length]);

  const registerRow = useCallback((nodeId, element) => {
    if (element) rowElementsRef.current.set(nodeId, element);
    else rowElementsRef.current.delete(nodeId);
  }, []);

  // Coming from the map, or jumping with the breadcrumb: open the path to that
  // place so its row exists, then scroll to it once it is rendered.
  useEffect(() => {
    if (!revealSignal || selectedId == null) return;
    const ancestors = listAncestorIds(nodes, selectedId);
    pendingRevealRef.current = String(selectedId);
    if (!ancestors.length) return;
    onExpandedChange?.(previous => {
      const next = new Set(previous ? Array.from(previous).map(String) : []);
      ancestors.forEach(id => next.add(id));
      return next;
    });
    logInfo(LOG_CONTEXT, 'storage list revealed a place', {
      nodeId: selectedId,
      openedAbove: ancestors.length,
    });
    // Only a new signal reveals; expanding a row by hand must not scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealSignal]);

  useEffect(() => {
    const nodeId = pendingRevealRef.current;
    if (!nodeId) return;
    const element = rowElementsRef.current.get(nodeId);
    if (!element) return;
    pendingRevealRef.current = null;
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest' });
    }
  }, [rows]);

  const searching = Boolean(query.trim());

  return (
    <div className="storage-list">
      <div className="storage-list__toolbar">
        <Form.Group className="storage-list__search">
          <Form.Label htmlFor="storage-list-search" className="visually-hidden">
            Search places
          </Form.Label>
          <Form.Control
            id="storage-list-search"
            type="search"
            size="sm"
            value={query}
            placeholder="Search a room, freezer, drawer, or box"
            onChange={event => setQuery(event.target.value)}
          />
        </Form.Group>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleExpandAll}
          disabled={!parentIds.length}
        >
          Expand all
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleCollapseAll}
          disabled={!parentIds.length}
        >
          Collapse all
        </Button>
        {typeof onAdd === 'function' && !addingRoom ? (
          <Button className="glass-primary-btn" size="sm" onClick={() => setAddingRoom(true)}>
            + Add room
          </Button>
        ) : null}
      </div>

      {typeof onAdd === 'function' && addingRoom ? (
        <div className="storage-list__add-room">
          <StorageAddForm
            parent={null}
            compact
            idPrefix="list-add-room"
            onAdd={async payload => {
              const ok = await onAdd?.(null, payload);
              if (ok !== false) setAddingRoom(false);
              return ok;
            }}
          />
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => setAddingRoom(false)}
          >
            Cancel
          </Button>
        </div>
      ) : null}

      {rows.length ? (
        <ul className="storage-list__rows">
          {rows.map(row => {
            const counts = rowCounts.get(row.id) || { childCount: 0, lotCount: null };
            return (
              <StorageNodeListRow
                key={row.id}
                node={row.node}
                depth={row.depth}
                hasChildren={row.hasChildren}
                expanded={row.expanded}
                selected={selectedId != null && String(selectedId) === row.id}
                childCount={counts.childCount}
                lotCount={counts.lotCount}
                nodes={nodes}
                onToggle={handleToggle}
                onOpen={handleOpen}
                onAdd={onAdd}
                onRemove={onRemove}
                onDeleted={onDeleted}
                onOpenBox={onOpenBox}
                onEditBox={onEditBox}
                onMove={onMove}
                onRename={onRename}
                onPlaceItem={onPlaceItem}
                onPreviewPlace={onPreviewPlace}
                onCancelPreview={onCancelPreview}
                rowRef={registerRow}
              />
            );
          })}
        </ul>
      ) : (
        <p className="storage-list__empty mb-0">
          {searching
            ? 'No place matches that search.'
            : 'No rooms yet. Add the first one to start the map.'}
        </p>
      )}
    </div>
  );
};

export default StorageNodeList;
